import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import {
  User,
  listAdmins,
  updateAdmin,
} from '../api/auth';
import AIModelManagement from './AIModelManagement';
import AdminAccountTab from './AdminAccountTab';
import AdminEnvTab from './AdminEnvTab';
import AdminNotionTab from './AdminNotionTab';
import AdminPsynetTab from './AdminPsynetTab';
import { commonStyles, colors, typography, spacing } from '../styles/design-system';
import { useAuth } from '../contexts/AuthContext';

interface AdminManagementProps {
  currentUser: User;
  onLogout?: () => void;
}

const AdminManagement: React.FC<AdminManagementProps> = ({ currentUser, onLogout }) => {
  const { updateUser } = useAuth();
  const [activeTab, setActiveTab] = useState<'accounts' | 'env' | 'notion' | 'ai_models' | 'psynet' | 'swagger'>('accounts');
  const [admins, setAdmins] = useState<(User & { is_active?: boolean; created_at?: string })[]>([]);

  // 비관리자 수정 모달 상태
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState<User | null>(null);
  const [editUsername, setEditUsername] = useState('');
  const [editDisplayName, setEditDisplayName] = useState('');
  const [editRole, setEditRole] = useState<'administrator' | 'manager'>('manager');
  const [editPassword, setEditPassword] = useState('');

  const isAdministrator = currentUser.role === 'administrator';

  useEffect(() => {
    if (isAdministrator) {
      fetchAdmins();
    }
  }, [isAdministrator]);

  const fetchAdmins = async () => {
    try {
      const data = await listAdmins();
      setAdmins(data as (User & { is_active?: boolean; created_at?: string })[]);
    } catch (error) {
      toast.error('관리자 목록을 불러오는데 실패했음.');
    }
  };

  const handleUpdateAdmin = async () => {
    if (!selectedAdmin) return;

    try {
      const updateData: any = {};
      if (editUsername !== selectedAdmin.username) updateData.username = editUsername;
      if (editDisplayName !== selectedAdmin.display_name) updateData.display_name = editDisplayName;
      if (editRole !== selectedAdmin.role) updateData.role = editRole;
      if (editPassword) updateData.new_password = editPassword;

      if (Object.keys(updateData).length === 0) {
        toast.info('변경된 내용이 없음.');
        return;
      }

      await updateAdmin(selectedAdmin.admin_id, updateData);
      toast.success('관리자 정보가 수정되었음.');
      setShowEditModal(false);

      if (selectedAdmin.admin_id === currentUser.admin_id) {
        updateUser({
          ...currentUser,
          username: editUsername,
          display_name: editDisplayName,
          role: editRole
        });
      }
    } catch (error: any) {
      toast.error(error.response?.data?.detail || '수정에 실패했음.');
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>

      </div>

      {isAdministrator && (
        <div style={styles.tabContainer}>
          <button
            style={activeTab === 'accounts' ? {...styles.tabButton, ...styles.activeTab} : styles.tabButton}
            onClick={() => setActiveTab('accounts')}
          >
            계정 관리
          </button>
          <button
            style={activeTab === 'env' ? {...styles.tabButton, ...styles.activeTab} : styles.tabButton}
            onClick={() => setActiveTab('env')}
          >
            환경변수 관리
          </button>
          <button
            style={activeTab === 'notion' ? {...styles.tabButton, ...styles.activeTab} : styles.tabButton}
            onClick={() => setActiveTab('notion')}
          >
            Notion 페이지 관리
          </button>
          <button
            style={activeTab === 'ai_models' ? {...styles.tabButton, ...styles.activeTab} : styles.tabButton}
            onClick={() => setActiveTab('ai_models')}
          >
            AI 모델 관리
          </button>
          <button
            style={activeTab === 'psynet' ? {...styles.tabButton, ...styles.activeTab} : styles.tabButton}
            onClick={() => setActiveTab('psynet')}
          >
            Psynet 발행
          </button>
          <button
            style={activeTab === 'swagger' ? {...styles.tabButton, ...styles.activeTab} : styles.tabButton}
            onClick={() => setActiveTab('swagger')}
          >
            스웨거
          </button>
          {onLogout && (
              <button
                  style={styles.logoutButton}
                  onClick={onLogout}
              >
                로그아웃
              </button>
          )}
        </div>
      )}

      {isAdministrator && activeTab === 'accounts' ? (
        <AdminAccountTab
          currentUser={currentUser}
          onUserUpdate={updateUser}
          isAdministrator={isAdministrator}
          admins={admins}
          fetchAdmins={fetchAdmins}
        />
      ) : isAdministrator && activeTab === 'env' ? (
        <AdminEnvTab
          currentUser={currentUser}
          admins={admins}
        />
      ) : isAdministrator && activeTab === 'ai_models' ? (
        <AIModelManagement />
      ) : isAdministrator && activeTab === 'notion' ? (
        <AdminNotionTab
          currentUser={currentUser}
          admins={admins}
        />
      ) : isAdministrator && activeTab === 'psynet' ? (
        <AdminPsynetTab
          currentUser={currentUser}
        />
      ) : isAdministrator && activeTab === 'swagger' ? (
        <div style={{ width: '100%', height: '700px', border: `1px solid ${colors.gray[200]}`, borderRadius: '4px', overflow: 'hidden' }}>
          <iframe
            src={`${process.env.REACT_APP_API_URL}/api/docs`}
            style={{ width: '100%', height: '100%', border: 'none' }}
            title="Swagger UI"
          />
        </div>
      ) : (
        <div style={styles.myAccountSection}>
          <h4 style={styles.myAccountTitle}>내 계정 정보</h4>
          <div style={styles.myAccountInfo}>
            <div style={styles.myAccountRow}>
              <span style={styles.myAccountLabel}>아이디:</span>
              <span style={styles.myAccountValue}>{currentUser.username}</span>
            </div>
            <div style={styles.myAccountRow}>
              <span style={styles.myAccountLabel}>표시 이름:</span>
              <span style={styles.myAccountValue}>{currentUser.display_name}</span>
            </div>
            <div style={styles.myAccountRow}>
              <span style={styles.myAccountLabel}>역할:</span>
              <span style={styles.myAccountValue}>매니저</span>
            </div>
          </div>
          <div style={styles.myAccountActions}>
            <button
              style={styles.editButton}
              onClick={() => {
                setSelectedAdmin(currentUser as User);
                setEditUsername(currentUser.username);
                setEditDisplayName(currentUser.display_name);
                setEditRole(currentUser.role);
                setEditPassword('');
                setShowEditModal(true);
              }}
            >
              내 정보 수정
            </button>
          </div>
        </div>
      )}

      {/* 비관리자 수정 모달 (매니저 전용) */}
      {showEditModal && selectedAdmin && (
        <div style={styles.modalBackdrop} onClick={() => setShowEditModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h3 style={styles.modalTitle}>내 정보 수정</h3>
            <div style={styles.formGroup}>
              <label style={styles.label}>표시 이름</label>
              <input
                type="text"
                value={editDisplayName}
                onChange={(e) => setEditDisplayName(e.target.value)}
                style={styles.input}
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>새 비밀번호</label>
              <input
                type="password"
                value={editPassword}
                onChange={(e) => setEditPassword(e.target.value)}
                style={styles.input}
                placeholder="변경 시에만 입력"
              />
            </div>
            <div style={styles.modalActions}>
              <button style={styles.cancelButton} onClick={() => setShowEditModal(false)}>
                취소
              </button>
              <button style={styles.submitButton} onClick={handleUpdateAdmin}>
                저장
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    ...commonStyles.section,
  },
  header: {},
  tabContainer: {
    ...commonStyles.tabContainer,
    marginBottom: spacing[5],
  },
  tabButton: {
    ...commonStyles.tabButton,
  },
  activeTab: {
    ...commonStyles.tabButtonActive,
  },
  logoutButton: {
    marginLeft: "auto",
    ...commonStyles.button,
    ...commonStyles.buttonSecondary,
  },
  editButton: {
    ...commonStyles.button,
    ...commonStyles.buttonSecondary,
    padding: `${spacing[1]} ${spacing[3]}`,
    fontSize: typography.fontSize.sm,
  },
  myAccountSection: {
    backgroundColor: colors.gray[50],
    border: `1px solid ${colors.gray[200]}`,
    padding: spacing[6],
  },
  myAccountTitle: {
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.semibold,
    color: colors.black,
    marginTop: 0,
    marginBottom: spacing[5],
  },
  myAccountInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing[3],
  },
  myAccountRow: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing[4],
  },
  myAccountLabel: {
    fontSize: typography.fontSize.base,
    color: colors.gray[600],
    minWidth: '80px',
  },
  myAccountValue: {
    fontSize: typography.fontSize.base,
    color: colors.black,
    fontWeight: typography.fontWeight.medium,
  },
  myAccountActions: {
    marginTop: spacing[6],
    display: 'flex',
    gap: spacing[3],
  },
  modalBackdrop: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  modalContent: {
    backgroundColor: colors.white,
    padding: spacing[8],
    minWidth: '400px',
    maxWidth: '500px',
    border: `1px solid ${colors.gray[200]}`,
  },
  modalTitle: {
    ...commonStyles.sectionTitle,
    marginBottom: spacing[6],
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing[2],
    marginBottom: spacing[5],
  },
  label: {
    ...commonStyles.label,
    minWidth: 'auto',
  },
  input: {
    ...commonStyles.input,
    width: '100%',
    boxSizing: 'border-box',
  },
  select: {
    ...commonStyles.select,
    width: '100%',
  },
  modalActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: spacing[3],
    marginTop: spacing[6],
  },
  cancelButton: {
    ...commonStyles.button,
    ...commonStyles.buttonSecondary,
  },
  submitButton: {
    ...commonStyles.button,
    ...commonStyles.buttonPrimary,
  },
};

export default AdminManagement;
